import { Ban, Download, LoaderCircle, Pause, Play, Search, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, downloadFile } from "./api";
import { EmptyState, LoadingState, Pagination, SectionHeading, StatusBadge, formatDate } from "./components";
import type { CompetitorCollectionItem, CompetitorCollectionRun } from "./types";

const PAGE_SIZE = 30;

function progress(run: CompetitorCollectionRun | null) {
  if (!run?.totalCount) return 0;
  return Math.min(100, Math.round((run.processedCount / run.totalCount) * 100));
}

export function CompetitorCollectionPanel({ accountCode }: { accountCode: string }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sampleSize, setSampleSize] = useState<0 | 20>(20);
  const [runs, setRuns] = useState<CompetitorCollectionRun[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [items, setItems] = useState<CompetitorCollectionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");

  const selected = useMemo(() => runs.find((run) => run.id === selectedId) ?? runs[0] ?? null, [runs, selectedId]);

  const loadRuns = useCallback(async () => {
    if (!accountCode) { setRuns([]); setLoading(false); return []; }
    const response = await api.competitorCollections(accountCode);
    setRuns(response.items);
    setSelectedId((current) => response.items.some((run) => run.id === current) ? current : response.items[0]?.id ?? "");
    return response.items;
  }, [accountCode]);

  const loadItems = useCallback(async () => {
    if (!selected?.id) { setItems([]); setTotal(0); setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (status) params.set("status", status);
    if (query) params.set("search", query);
    try {
      const response = await api.competitorCollectionItems(selected.id, params);
      setItems(response.items); setTotal(response.total);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "竞品明细加载失败。");
    } finally { setLoading(false); }
  }, [page, query, selected?.id, status]);

  useEffect(() => {
    setLoading(true); setRuns([]); setSelectedId(""); setItems([]); setPage(1);
    void loadRuns().catch((error) => { setMessage(error instanceof Error ? error.message : "竞品任务加载失败。"); setLoading(false); });
  }, [accountCode, loadRuns]);

  useEffect(() => { void loadItems(); }, [loadItems]);

  useEffect(() => {
    if (!selected || !["QUEUED", "RUNNING"].includes(selected.status)) return;
    let stopped = false;
    const timer = window.setInterval(() => void (async () => {
      try {
        const next = await api.competitorCollection(selected.id);
        if (stopped) return;
        setRuns((current) => current.map((run) => run.id === next.id ? next : run));
        if (!["QUEUED", "RUNNING"].includes(next.status)) {
          await loadRuns(); await loadItems();
          if (!stopped) setMessage(`竞品采集已结束：${next.status}，可下载结果。`);
        }
      } catch (error) {
        if (!stopped) setMessage(error instanceof Error ? error.message : "采集进度读取失败。");
      }
    })(), 1500);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [loadItems, loadRuns, selected]);

  async function start() {
    if (!file || !accountCode) return;
    setBusy("START"); setMessage("");
    try {
      const run = await api.startCompetitorCollection({ accountCode, file, sampleSize, maxCompetitors: 5 });
      setRuns((current) => [run, ...current.filter((entry) => entry.id !== run.id)]);
      setSelectedId(run.id); setFile(null); setPage(1);
      if (fileRef.current) fileRef.current.value = "";
      setMessage(sampleSize === 20 ? "20 条跨站点样本已进入采集队列。" : "全量竞品采集已进入队列，可离开页面后再回来查看。" );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "竞品采集无法启动。");
    } finally { setBusy(""); }
  }

  async function action(value: "pause" | "resume" | "cancel") {
    if (!selected) return;
    setBusy(value); setMessage("");
    try {
      const run = await api.competitorCollectionAction(selected.id, value);
      setRuns((current) => current.map((entry) => entry.id === run.id ? run : entry));
      setMessage(value === "pause" ? "采集已暂停。" : value === "resume" ? "采集已继续。" : "采集已中止。" );
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "任务操作失败。");
    } finally { setBusy(""); }
  }

  async function download() {
    if (!selected) return;
    setBusy("DOWNLOAD"); setMessage("");
    try {
      await downloadFile(`/api/competitor-collections/${encodeURIComponent(selected.id)}/export`, `Mercado_Libre_竞品数据_${selected.id}.xlsx`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "结果下载失败。");
    } finally { setBusy(""); }
  }

  const percentage = progress(selected);
  return <section className="workspace-band competitor-collection">
    <SectionHeading title="竞品 API 采集" meta={selected ? `${selected.processedCount.toLocaleString()} / ${selected.totalCount.toLocaleString()} · ${selected.status}` : "上传竞品计价模板"} />
    {message ? <div className="inline-message" role="status">{message}</div> : null}
    <div className="competitor-upload-bar">
      <label className="file-picker">
        <Upload size={17} />
        <span>{file?.name ?? "选择竞品分析模板"}</span>
        <input ref={fileRef} type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} />
      </label>
      <div className="segmented-control" aria-label="采集范围">
        <button type="button" className={sampleSize === 20 ? "active" : ""} onClick={() => setSampleSize(20)}>先测 20 条</button>
        <button type="button" className={sampleSize === 0 ? "active" : ""} onClick={() => setSampleSize(0)}>全量</button>
      </div>
      <button className="button primary" disabled={!file || !accountCode || busy !== ""} onClick={() => void start()}>
        {busy === "START" ? <LoaderCircle size={17} className="spin" /> : <Play size={17} />}开始采集
      </button>
      {selected?.status === "RUNNING" ? <button className="icon-button" title="暂停" aria-label="暂停竞品采集" disabled={busy !== ""} onClick={() => void action("pause")}><Pause size={17} /></button> : null}
      {selected?.status === "PAUSED" ? <button className="icon-button" title="继续" aria-label="继续竞品采集" disabled={busy !== ""} onClick={() => void action("resume")}><Play size={17} /></button> : null}
      {selected && ["QUEUED", "RUNNING", "PAUSED"].includes(selected.status) ? <button className="icon-button danger" title="中止" aria-label="中止竞品采集" disabled={busy !== ""} onClick={() => void action("cancel")}><Ban size={17} /></button> : null}
      <button className="button secondary" disabled={!selected || !["SUCCESS", "PARTIAL", "FAILED"].includes(selected.status) || busy !== ""} onClick={() => void download()}>
        {busy === "DOWNLOAD" ? <LoaderCircle size={17} className="spin" /> : <Download size={17} />}下载结果
      </button>
    </div>

    {selected ? <>
      <div className="competitor-run-strip">
        <div><span>匹配商品</span><strong>{selected.successCount.toLocaleString()}</strong></div>
        <div><span>有效竞品记录</span><strong>{selected.competitorCount.toLocaleString()}</strong></div>
        <div><span>无有效竞品</span><strong>{selected.noMatchCount.toLocaleString()}</strong></div>
        <div><span>接口失败</span><strong>{selected.failedCount.toLocaleString()}</strong></div>
        <select aria-label="采集批次" value={selected.id} onChange={(event) => { setSelectedId(event.target.value); setPage(1); }}>
          {runs.map((run) => <option key={run.id} value={run.id}>{formatDate(run.createdAt)} · {run.status} · {run.totalCount} 条</option>)}
        </select>
      </div>
      {selected.status === "RUNNING" ? <div className="insight-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentage}><span style={{ width: `${percentage}%` }} /><strong>{percentage}%</strong></div> : null}
      <div className="filter-bar competitor-filter">
        <label className="search-field"><Search size={16} /><input aria-label="搜索竞品采集商品" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setPage(1); setQuery(search.trim()); } }} placeholder="SKU / CBT / Item ID" /></label>
        <select aria-label="采集状态" value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
          <option value="">全部状态</option><option value="SUCCESS">已匹配</option><option value="NO_MATCH">无有效竞品</option><option value="FAILED">接口失败</option><option value="PENDING">待处理</option>
        </select>
        <button className="button secondary" onClick={() => { setPage(1); setQuery(search.trim()); }}>查询</button>
      </div>
      {loading ? <LoadingState /> : items.length ? <>
        <div className="table-scroll"><table className="data-table competitor-table"><thead><tr><th>SKU / 商品</th><th>站点</th><th>目录商品</th><th>有效竞品</th><th>状态</th><th>原因</th><th>更新时间</th></tr></thead><tbody>
          {items.map((item) => <tr key={`${item.runId}:${item.ownItemId}`}><td><strong>{item.merchantSku || "--"}</strong><small>{item.parentItemId} · {item.ownItemId}</small><small className="truncate" title={item.title}>{item.title}</small></td><td>{item.siteCode}</td><td className="mono">{item.catalogProductId || "--"}</td><td className="mono"><strong>{item.competitorCount}</strong></td><td><StatusBadge status={item.status === "SUCCESS" ? "SUCCESS" : item.status === "FAILED" ? "FAILED" : item.status === "NO_MATCH" ? "NEEDS_REVIEW" : "RUNNING"} /></td><td className="failure-detail" title={item.errorMessage ?? ""}>{item.errorCode ?? "--"}<small>{item.errorMessage ?? ""}</small></td><td>{formatDate(item.updatedAt)}</td></tr>)}
        </tbody></table></div><Pagination page={page} total={total} pageSize={PAGE_SIZE} onChange={setPage} />
      </> : <EmptyState title="该批次暂无商品明细" />}
    </> : loading ? <LoadingState /> : <EmptyState title="尚未创建竞品采集任务" />}
  </section>;
}
